import { useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { AutoComplete } from "primereact/autocomplete";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useNavigate, useParams } from "react-router-dom";
import expenseService from "../../services/ExpenseService";
import expenseCategoryService from "../../services/ExpenseCategoryService";
import FinancialAccountService from "../../services/FinancialAccountService";
import { employeeService } from "../../services/EmployeeService";

export default function ExpenseEditPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const { id } = useParams();
    const [expense, setExpense] = useState({
        id: 0,
        description: "",
        amount: 0,
        expenseDate: new Date(),
        isRecurring: false,
        expenseCategoryId: 0,
        expenseCategoryName: "",
        financialAccountId: null,
        vatRate: 20,
        vatAmount: 0,
        documentNo: "",
        documentType: "Fiş",
        isPaid: true,
        isPaidByEmployee: false,
        employeeId: null,
        installmentCount: 1,
        firstInstallmentDate: new Date()
    });

    const [categories, setCategories] = useState([]);
    const [financialAccounts, setFinancialAccounts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const toast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            const [catRes, expRes, accRes, empRes] = await Promise.all([
                expenseCategoryService.getAll(),
                expenseService.getById(id),
                FinancialAccountService.getAll(),
                employeeService.list()
            ]);

            setCategories(catRes.data || catRes);
            if (accRes.success) setFinancialAccounts((accRes.data || []).filter(a => a.accountType !== 5));
            setEmployees(empRes.data?.data ?? empRes.data ?? []);

            const result = expRes.data || expRes;
            if (result.success) {
                setExpense({
                    ...result.data,
                    expenseDate: new Date(result.data.expenseDate),
                    documentType: result.data.documentType || "Fiş",
                    documentNo: result.data.documentNo || "",
                    vatRate: result.data.vatRate || 0,
                    vatAmount: result.data.vatAmount || 0,
                    isPaid: result.data.isPaid !== undefined ? result.data.isPaid : true,
                    isPaidByEmployee: result.data.isPaidByEmployee || false,
                    employeeId: result.data.employeeId || null,
                    installmentCount: result.data.installmentCount || 1,
                    firstInstallmentDate: result.data.firstInstallmentDate ? new Date(result.data.firstInstallmentDate) : new Date()
                });
            } else {
                toast.current.show({ severity: "error", summary: "Hata", detail: "Gider yüklenemedi!" });
            }
        } catch (error) {
            console.error("Data load error", error);
        }
    };

    const searchCategories = (event) => {
        const query = event.query.toLowerCase();
        let _filtered = categories.filter(c => c.name.toLowerCase().includes(query));

        if (_filtered.length === 0 && query.length > 0) {
            _filtered = [{ id: 0, name: event.query, isNew: true }];
        }

        setFilteredCategories(_filtered);
    };

    const handleAmountOrVatChange = (amount, vatRate) => {
        const amt = amount || 0;
        const rate = vatRate || 0;
        const calculatedVat = Math.round((amt * rate / (100 + rate)) * 100) / 100;
        setExpense(prev => ({
            ...prev,
            amount: amt,
            vatRate: rate,
            vatAmount: calculatedVat
        }));
    };

    const handleVatAmountChange = (val) => {
        setExpense(prev => ({
            ...prev,
            vatAmount: val || 0
        }));
    };

    const accountOptionTemplate = (option) => {
        if (!option) return <span>Hesap Seçin...</span>;

        let icon = "pi pi-money-bill text-green-500";
        let badgeColor = "bg-green-50 text-green-700 border-green-200";
        let typeText = "Nakit";

        if (Number(option.accountType) === 2) {
            icon = "pi pi-building text-blue-500";
            badgeColor = "bg-blue-50 text-blue-700 border-blue-200";
            typeText = "Banka";
        } else if (Number(option.accountType) === 3) {
            icon = "pi pi-credit-card text-purple-500";
            badgeColor = "bg-purple-50 text-purple-700 border-purple-200";
            typeText = "Kredi Kartı";
        }

        return (
            <div className="flex items-center justify-between w-full py-0.5">
                <div className="flex items-center gap-2">
                    <i className={icon}></i>
                    <span className="font-bold text-slate-700 dark:text-slate-300">{option.name}</span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-md border font-black ${badgeColor}`}>
                    {typeText}
                </span>
            </div>
        );
    };

    const selectedAccountTemplate = (option, props) => {
        if (!option) return <span>{props.placeholder}</span>;

        let icon = "pi pi-money-bill text-green-500";
        if (Number(option.accountType) === 2) {
            icon = "pi pi-building text-blue-500";
        } else if (Number(option.accountType) === 3) {
            icon = "pi pi-credit-card text-purple-500";
        }

        return (
            <div className="flex items-center gap-2">
                <i className={icon}></i>
                <span>{option.name}</span>
            </div>
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!expense.expenseCategoryName) {
            toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Gider başlığı seçiniz!" });
            return;
        }

        if (expense.amount <= 0) {
            toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Tutar 0'dan büyük olmalıdır!" });
            return;
        }

        if (expense.isPaid && !expense.isPaidByEmployee && !expense.financialAccountId) {
            toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Ödemenin yapılacağı hesabı seçiniz!" });
            return;
        }

        if (expense.isPaidByEmployee && !expense.employeeId) {
            toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Ödemeyi yapan çalışanı seçiniz!" });
            return;
        }

        try {
            setLoading(true);
            const res = await expenseService.update(expense);
            const result = res.data || res;
            if (result.success) {
                toast.current.show({ severity: "success", summary: "Başarılı", detail: "Gider güncellendi." });
                setTimeout(() => navigate("/expenses"), 1500);
            } else {
                toast.current.show({ severity: "error", summary: "Hata", detail: result.message || "Hata oluştu!" });
            }
        } catch (error) {
            toast.current.show({ severity: "error", summary: "Hata", detail: "İşlem başarısız!" });
        } finally {
            setLoading(false);
        }
    };

    const selectedAccount = financialAccounts.find(a => a.id === expense.financialAccountId);
    const isCreditCard = selectedAccount && selectedAccount.accountType === 3;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-indigo-50/30 dark:from-slate-950 dark:to-slate-900 py-6 px-4 flex items-center justify-center relative overflow-hidden">
            {/* Ambient Background Glows */}
            <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-200/40 rounded-full blur-3xl dark:bg-indigo-950/20"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl dark:bg-purple-950/10"></div>

            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

            <div className="w-full max-w-3xl relative z-10">
                <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-white/50 dark:border-slate-800/50 rounded-3xl shadow-2xl shadow-slate-200/50 dark:shadow-none overflow-hidden">

                    {/* Header */}
                    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 p-6 text-white relative">
                        <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-2xl"></div>
                        <div className="flex items-center gap-4">
                            <div className="w- h-24 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center shadow-lg transform hover:rotate-6 transition-all duration-300">
                                <i className="pi pi-pencil text-2xl"></i>
                            </div>
                            <div>
                                <h1 className="text-2xl font-black uppercase tracking-tight">Gider Kaydı Düzenle</h1>
                                <p className="text-indigo-100 text-xs font-medium mt-0.5">Gider kaydını güncelleyin ve kaydedin.</p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
                        {/* Section 1: Genel Bilgiler */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                                <i className="pi pi-file text-indigo-500 text-sm font-bold"></i>
                                <h2 className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest">Genel Gider Bilgileri</h2>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Gider Başlığı */}
                                <div className="space-y-1">
                                    <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Gider Başlığı</label>
                                    <AutoComplete
                                        value={expense.expenseCategoryName}
                                        suggestions={filteredCategories}
                                        completeMethod={searchCategories}
                                        field="name"
                                        onChange={(e) => {
                                            const val = e.value;
                                            if (typeof val === 'string') {
                                                setExpense({ ...expense, expenseCategoryName: val, expenseCategoryId: 0 });
                                            } else {
                                                setExpense({ ...expense, expenseCategoryName: val.name, expenseCategoryId: val.id });
                                            }
                                        }}
                                        placeholder="Bir başlık seçin veya yeni yazın..."
                                        className="w-full"
                                        inputClassName="!w-full !py-2.5 !px-4 !rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white focus:border-indigo-500 transition-all font-semibold text-slate-700"
                                    />
                                </div>

                                {/* Gider Tarihi */}
                                <div className="space-y-1">
                                    <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Gider Tarihi</label>
                                    <Calendar
                                        value={expense.expenseDate}
                                        onChange={(e) => setExpense({ ...expense, expenseDate: e.value })}
                                        dateFormat="dd/mm/yy"
                                        locale="tr"
                                        showIcon
                                        appendTo={document.body}
                                        className="w-full"
                                        inputClassName="!py-2.5 !px-4 !rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white focus:border-indigo-500 transition-all font-semibold text-slate-700"
                                    />
                                </div>

                                {/* Belge Türü */}
                                <div className="space-y-1">
                                    <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Belge Türü</label>
                                    <Dropdown
                                        value={expense.documentType}
                                        options={[
                                            { label: "Fiş", value: "Fiş" },
                                            { label: "Fatura", value: "Fatura" },
                                            { label: "Makbuz", value: "Makbuz" },
                                            { label: "Diğer", value: "Diğer" }
                                        ]}
                                        onChange={(e) => setExpense({ ...expense, documentType: e.value })}
                                        className="w-full h-[44px] flex items-center rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white font-semibold"
                                    />
                                </div>

                                {/* Belge No */}
                                <div className="space-y-1">
                                    <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Belge No</label>
                                    <InputText
                                        value={expense.documentNo}
                                        onChange={(e) => setExpense({ ...expense, documentNo: e.target.value })}
                                        className="w-full !py-2.5 !px-4 !rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white focus:border-indigo-500 transition-all font-semibold text-slate-700"
                                        placeholder="Örn: ABC202600001"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Finansal Bilgiler */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                                <i className="pi pi-percentage text-indigo-500 text-sm font-bold"></i>
                                <h2 className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest">Tutar ve Vergi Detayları</h2>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {/* Tutar */}
                                <div className="space-y-1">
                                    <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Toplam Tutar (KDV DAHİL)</label>
                                    <InputNumber
                                        value={expense.amount}
                                        onValueChange={(e) => handleAmountOrVatChange(e.value, expense.vatRate)}
                                        mode="currency"
                                        currency="TRY"
                                        locale="tr-TR"
                                        className="w-full"
                                        inputClassName="!w-full !py-2.5 !px-4 !rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white focus:border-indigo-500 transition-all font-black text-indigo-600 text-lg"
                                    />
                                </div>

                                {/* KDV Oranı */}
                                <div className="space-y-1">
                                    <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">KDV Oranı</label>
                                    <Dropdown
                                        value={expense.vatRate}
                                        options={[
                                            { label: "%20", value: 20 },
                                            { label: "%10", value: 10 },
                                            { label: "%1", value: 1 },
                                            { label: "%0", value: 0 }
                                        ]}
                                        onChange={(e) => handleAmountOrVatChange(expense.amount, e.value)}
                                        className="w-full h-[44px] flex items-center rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white font-semibold"
                                    />
                                </div>

                                {/* KDV Tutarı */}
                                <div className="space-y-1">
                                    <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Hesaplanan KDV</label>
                                    <InputNumber
                                        value={expense.vatAmount}
                                        onValueChange={(e) => handleVatAmountChange(e.value)}
                                        mode="currency"
                                        currency="TRY"
                                        locale="tr-TR"
                                        className="w-full"
                                        inputClassName="!w-full !py-2.5 !px-4 !rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white focus:border-indigo-500 transition-all font-semibold text-slate-700"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center bg-indigo-50/50 dark:bg-indigo-950/10 p-3 rounded-xl border border-indigo-100/50 dark:border-indigo-900/20">
                                <Checkbox
                                    inputId="recurring"
                                    checked={expense.isRecurring}
                                    onChange={(e) => setExpense({ ...expense, isRecurring: e.checked })}
                                />
                                <label htmlFor="recurring" className="ml-2 text-[11px] font-bold text-slate-700 dark:text-slate-300 cursor-pointer uppercase tracking-wider">
                                    Aylık Düzenli Ödeme Olarak Ekle (Yinelenen Gider)
                                </label>
                            </div>
                        </div>

                        {/* Section 3: Ödeme Bilgileri */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                                <i className="pi pi-credit-card text-indigo-500 text-sm font-bold"></i>
                                <h2 className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest">Ödeme Yöntemi ve Kaynağı</h2>
                            </div>

                            <div className="bg-slate-50/50 dark:bg-slate-900/40 p-4 rounded-2xl border border-indigo-100 dark:border-slate-800 space-y-4">
                                {/* Çalışan Cebinden Ödendi Checkbox */}
                                <div className="flex items-center">
                                    <Checkbox
                                        inputId="isPaidByEmployee"
                                        checked={expense.isPaidByEmployee}
                                        onChange={(e) => {
                                            const checked = e.checked;
                                            setExpense(prev => ({
                                                ...prev,
                                                isPaidByEmployee: checked,
                                                isPaid: checked ? true : prev.isPaid,
                                                financialAccountId: checked ? null : prev.financialAccountId
                                            }));
                                        }}
                                    />
                                    <label htmlFor="isPaidByEmployee" className="ml-2 text-[11px] font-bold text-slate-700 dark:text-slate-300 cursor-pointer uppercase tracking-wider">
                                        Çalışan Kendi Cebinden Ödedi
                                    </label>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {expense.isPaidByEmployee ? (
                                        <div className="space-y-1">
                                            <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Ödeyen Personel</label>
                                            <Dropdown
                                                value={expense.employeeId}
                                                options={employees}
                                                onChange={(e) => setExpense({ ...expense, employeeId: e.value })}
                                                optionLabel="fullName"
                                                optionValue="id"
                                                placeholder="Çalışan seçin..."
                                                className="w-full h-[44px] flex items-center rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white font-semibold"
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="space-y-1">
                                                <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Ödeme Durumu</label>
                                                <Dropdown
                                                    value={expense.isPaid}
                                                    options={[
                                                        { label: "Ödendi", value: true },
                                                        { label: "Sonra Ödenecek (Borç/Fatura)", value: false }
                                                    ]}
                                                    onChange={(e) => setExpense(prev => ({
                                                        ...prev,
                                                        isPaid: e.value,
                                                        financialAccountId: e.value ? prev.financialAccountId : null
                                                    }))}
                                                    className="w-full h-[44px] flex items-center rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white font-semibold"
                                                />
                                            </div>

                                            {expense.isPaid && (
                                                <div className="space-y-1">
                                                    <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Ödeme Yapılacak Kasa/Banka</label>
                                                    <Dropdown
                                                        value={expense.financialAccountId}
                                                        options={financialAccounts}
                                                        onChange={(e) => setExpense({ ...expense, financialAccountId: e.value })}
                                                        optionLabel="name"
                                                        optionValue="id"
                                                        placeholder="Hesap seçin..."
                                                        itemTemplate={accountOptionTemplate}
                                                        valueTemplate={selectedAccountTemplate}
                                                        className="w-full h-[44px] flex items-center rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white font-semibold"
                                                    />
                                                </div>
                                            )}

                                            {expense.isPaid && isCreditCard && (
                                                <div className="bg-emerald-50/20 dark:bg-emerald-950/10 border border-emerald-100/50 dark:border-emerald-900/30 rounded-2xl p-4 space-y-4 col-span-1 sm:col-span-2 animate-fade-in">
                                                    <div className="text-[10px] font-black text-emerald-700 dark:text-emerald-400 uppercase tracking-widest flex items-center gap-2">
                                                        <i className="pi pi-clock"></i> TAKSİT SEÇENEKLERİ
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">TAKSİT SAYISI</label>
                                                            <InputNumber
                                                                value={expense.installmentCount}
                                                                onValueChange={(e) => setExpense(prev => ({ ...prev, installmentCount: e.value || 1 }))}
                                                                min={1}
                                                                max={36}
                                                                showButtons
                                                                buttonLayout="horizontal"
                                                                decrementButtonClassName="p-button-secondary p-button-outlined"
                                                                incrementButtonClassName="p-button-secondary p-button-outlined"
                                                                incrementButtonIcon="pi pi-plus"
                                                                decrementButtonIcon="pi pi-minus"
                                                                className="w-full"
                                                                inputClassName="text-center font-bold"
                                                            />
                                                        </div>
                                                        {expense.installmentCount > 1 && (
                                                            <div className="space-y-1">
                                                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">İLK TAKSİT TARİHİ</label>
                                                                <Calendar
                                                                    value={expense.firstInstallmentDate}
                                                                    onChange={(e) => setExpense(prev => ({ ...prev, firstInstallmentDate: e.value }))}
                                                                    dateFormat="dd.mm.yy"
                                                                    locale="tr"
                                                                    showIcon
                                                                    className="w-full"
                                                                    inputClassName="font-bold py-3 rounded-2xl border-slate-200"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Açıklama */}
                        <div className="space-y-1">
                            <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1">Gider Açıklaması / Detaylar</label>
                            <InputText
                                value={expense.description}
                                onChange={(e) => setExpense({ ...expense, description: e.target.value })}
                                className="w-full !py-2.5 !px-4 !rounded-xl border-slate-200 dark:border-slate-800 dark:bg-slate-950 dark:text-white focus:border-indigo-500 transition-all font-medium text-slate-600 dark:text-slate-300"
                                placeholder="Gider hakkında eklemek istediğiniz detaylar..."
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100 dark:border-slate-800/50">
                            <Button
                                type="button"
                                label="İPTAL ET"
                                icon="pi pi-times"
                                className="flex-1 p-button-text !text-slate-400 dark:!text-slate-500 font-black !py-3 !rounded-xl hover:!bg-slate-50 dark:hover:!bg-slate-900 transition-all"
                                onClick={() => navigate("/expenses")}
                            />
                            <Button
                                type="submit"
                                label="DEĞİŞİKLİKLERİ KAYDET"
                                icon="pi pi-check"
                                loading={loading}
                                className="flex-[2] !bg-gradient-to-r !from-indigo-600 !to-purple-600 !border-none font-black !py-3 !rounded-xl shadow-lg shadow-indigo-600/20 hover:shadow-indigo-600/30 hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 text-white"
                            />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
